import type { ApplicationHealthIncident, ApplicationHealthIncidentFilters } from './api/applicationHealth'
import { getApiBaseUrl, getDashboardEnvironment } from '../utils/env'

type ApplicationHealthReportOptions = {
  scope: 'displayed' | 'single'
  filters: ApplicationHealthIncidentFilters
  hasMore: boolean
}

export const buildApplicationHealthReport = (
  incidents: ApplicationHealthIncident[],
  { scope, filters, hasMore }: ApplicationHealthReportOptions
) => JSON.stringify({
  report: 'HikeStrong application health investigation',
  copiedAt: new Date().toISOString(),
  source: {
    dashboardEnvironment: getDashboardEnvironment(),
    dashboardOrigin: window.location.origin,
    apiOrigin: new URL(getApiBaseUrl(), window.location.origin).origin,
    endpoint: '/api/admin/application-health/incidents',
    scope: scope === 'single' ? 'Single incident' : 'Displayed incidents matching the current filters',
    filters,
    incidentCount: incidents.length,
    hasMoreMatchingIncidents: hasMore
  },
  incidents: incidents.map((incident) => ({
    correlationReference: incident.correlationReference,
    status: incident.status,
    reasonCode: incident.reasonCode,
    httpStatus: incident.httpStatus,
    httpMethod: incident.httpMethod,
    routeTemplate: incident.routeTemplate,
    occurrenceCount: incident.occurrenceCount,
    firstSeenAt: incident.firstSeenAt,
    lastSeenAt: incident.lastSeenAt,
    sentrySearch: `application_health_correlation:${incident.correlationReference}`
  })),
  investigation: {
    grouping: 'Each correlation reference identifies a group with the same reason code, HTTP status, method, and route template. It is not an individual request or Sentry event ID.',
    timestamps: 'firstSeenAt and lastSeenAt are UTC timestamps spanning the recorded occurrences.',
    steps: [
      'Use the API origin to identify the affected environment and confirm the backend release deployed at the incident timestamps; this report does not contain the backend release.',
      'Find the matching HTTP method and route template in backend/Controllers or backend/Program.cs, then follow the handler into backend/Services and its dependencies.',
      'Search backend logs for the correlationReference within the firstSeenAt/lastSeenAt window. The recorder logs "Application health incident {CorrelationReference} recorded for {ReasonCode}, HTTP {HttpStatus} {HttpMethod} {RouteTemplate}".',
      'Search Sentry with the incident sentrySearch tag in the same environment and time window, when a matching event is available. Inspect the original exception and stack trace there.',
      'Check classification in backend/Middleware/ApplicationHealthIncidentMiddleware.cs: MongoException maps to data_store_failure; TimeoutException/TaskCanceledException to timeout; HttpRequestException to dependency_failure. HTTP 502/503 responses map to dependency_failure and 504 to timeout. Other exceptions map to unhandled_exception and other 5xx responses to server_error_response.',
      'Treat the reason code as a classification hint. Reproduce the failing route and inspect downstream dependencies before determining the root cause.'
    ],
    sourcePaths: [
      'backend/Middleware/ApplicationHealthIncidentMiddleware.cs',
      'backend/Services/ApplicationHealthIncidentService.cs',
      'backend/Controllers/AdminApplicationHealthController.cs',
      'backend/Models/ApplicationHealthIncident.cs'
    ],
    availableDetails: 'The inbox retains bounded operational metadata only. Exception messages, stack traces, request URLs, query strings, bodies, credentials, and user/device/health data are not included. Use authorized backend logs or Sentry for further evidence.'
  }
}, null, 2)
